import { SelectOptionItemsType } from 'components/SelectOption'
import { StateType } from 'data/types'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import { createSelector } from 'reselect'
import { getBaseCurrency, getCounterCurrency } from 'utils/pairs'

import {
  CurrencyDefinitionType,
  FormattedInstrumentType,
  InstrumentType,
  parseSymbol,
  PrecisionType,
  serialiseSymbol,
  SymbolType
} from './types'

const getInstrument = (state: StateType) => state.instrument

const toFormattedName: (CurrencyDefinitionType) => string = ({ key, name }) => {
  if (name) {
    return `${name} (${key})`
  }

  return key
}

const toListItem = (currency) => ({
  text: currency.name,
  value: currency.key
})

const toCompactListItem = (currency) => ({
  text: currency.key,
  value: currency.key
})

const toNamedCurrencyList = (currency) => ({
  ...currency,
  name: toFormattedName(currency)
})

const mapToKey = (currency) => currency.key

const getRawCurrencies = (state: StateType) => getInstrument(state).currencies

const getCurrencies: (state: StateType) => CurrencyDefinitionType[] = createSelector(
  getRawCurrencies,
  (curs) => sortBy(uniqBy(curs, (c) => c.key).map(toNamedCurrencyList), ['name'])
)

const getCurrenciesList: (state: StateType) => SelectOptionItemsType = createSelector(
  getCurrencies,
  (curs) => curs.map(toListItem)
)

const getCurrenciesIndexed: (state: StateType) => {
  [currency: string]: CurrencyDefinitionType
} = createSelector(getCurrencies, (curs) =>
  curs.reduce((acc, curr) => ({ ...acc, [curr.key]: curr }), {})
)

const getInstruments = createSelector(
  getRawCurrencies,
  (state: StateType) => state.instrument.all,
  (currencies, instruments) =>
    instruments.filter((i) => {
      const base = getBaseCurrency(i.symbol)
      const counter = getCounterCurrency(i.symbol)
      // If the currency (base or counter) is listed in markets currencies
      // and is blocked, filter it out. If it's not there, it's ok
      if (currencies.find((c) => c.key === base && c.blocked)) return false
      if (currencies.find((c) => c.key === counter && c.blocked)) return false
      return true
    })
)

type IndexedInstruments = {
  [symbol: string]: InstrumentType
}
const getInstrumentsIndexed: (state: StateType) => IndexedInstruments = createSelector(
  getInstruments,
  (instruments) => instruments.reduce((acc, curr) => ({ ...acc, [curr.symbol]: curr }), {})
)

const getFiatCurrencies: (state: StateType) => Array<string> = createSelector(
  getRawCurrencies,
  (curs) =>
    uniqBy(
      curs.filter((cur: CurrencyDefinitionType) => cur.fiat),
      (x) => x.key
    ).map(mapToKey)
)

const getFiatCurrenciesList: (state: StateType) => SelectOptionItemsType = createSelector(
  getCurrencies,
  (curs) => curs.filter((c: CurrencyDefinitionType) => c.fiat).map(toListItem)
)

const getFiatCurrenciesListCompact: (state: StateType) => SelectOptionItemsType = createSelector(
  getCurrencies,
  (curs) => curs.filter((c: CurrencyDefinitionType) => c.fiat).map(toCompactListItem)
)

const getAll: (state: StateType) => Array<FormattedInstrumentType> = createSelector(
  (state: StateType) => state.instrument.all,
  getCurrenciesIndexed,
  (all, currencies) =>
    sortBy(
      all
        .filter((instrument: InstrumentType) => instrument.status !== 'closed')
        .map((instrument) => {
          const baseCurrency = currencies[instrument.base_currency]
          const baseName = baseCurrency ? currencies[instrument.base_currency].name : ''
          const counterCurrency = currencies[instrument.counter_currency]
          const counterName = counterCurrency ? currencies[instrument.counter_currency].name : ''

          return {
            base: instrument.base_currency,
            baseName,
            counter: instrument.counter_currency,
            counterName,
            id: instrument.id,
            symbol: instrument.symbol,
            text: `${instrument.base_currency}-${instrument.counter_currency}`,
            value: serialiseSymbol(instrument.symbol)
          }
        }),
      ['text']
    )
)

const getSymbolTyped: (state: StateType) => SymbolType = createSelector(
  getInstrument,
  (state) => state.symbol
)

const getSymbol: (state: StateType) => string = createSelector(getInstrument, (state) =>
  serialiseSymbol(state.symbol)
)

const getCurrentInstrument: (state: StateType) => InstrumentType | undefined = createSelector(
  (state: StateType) => state.instrument.all,
  getSymbolTyped,
  (all, currentSymbol) => all.find(({ symbol }) => symbol === currentSymbol)
)

const getPreviousSymbolTyped: (state: StateType) => SymbolType = createSelector(
  getInstrument,
  (state) => state.previousSymbol
)

const getPreviousInstrument: (state: StateType) => InstrumentType | undefined = createSelector(
  (state: StateType) => state.instrument.all,
  getPreviousSymbolTyped,
  (all, currentSymbol) => all.find(({ symbol }) => symbol === currentSymbol)
)

const getInstrumentId: (state: StateType) => number | undefined = createSelector(
  getCurrentInstrument,
  (instrument) => (instrument || {}).id
)

const getBaseSymbol: (state: StateType) => string = createSelector(
  getSymbolTyped,
  (current) => parseSymbol(current).base
)

const getBaseSymbolDefinition: (state: StateType) => undefined | CurrencyDefinitionType =
  createSelector(getBaseSymbol, getCurrenciesIndexed, (symbol, currencies) => currencies[symbol])

const getCounterSymbol: (state: StateType) => string = createSelector(
  getSymbolTyped,
  (current) => parseSymbol(current).counter
)

const getCounterSymbolDefinition: (state: StateType) => undefined | CurrencyDefinitionType =
  createSelector(getCounterSymbol, getCurrenciesIndexed, (symbol, currencies) => currencies[symbol])

const getFavorites: (state: StateType) => string[] = createSelector(getInstrument, (instrument) =>
  instrument.favorites.map(serialiseSymbol).filter((fav) => fav !== '')
)

const getPrecision: (state: StateType) => PrecisionType = createSelector(
  getCurrentInstrument,
  (instrument: any) => ({
    amount: instrument?.volume_increment ?? 2,
    min_amount: instrument?.min_order_size_calc ?? 0,
    price: instrument?.price_increment ?? 2
  })
)

const isSymbolBlocked: (state: StateType) => boolean = createSelector(
  getBaseSymbolDefinition,
  getCounterSymbolDefinition,
  (base, counter) => {
    const baseBlocked = base ? base.blocked : false
    const counterBlocked = counter ? counter.blocked : false

    return baseBlocked || counterBlocked
  }
)

const getCurrentLeverageRatio: (state: StateType) => number[] = createSelector(
  getCurrentInstrument,
  (instrument) => instrument?.leverage_ratio || []
)

export default {
  getAll,
  getBaseSymbol,
  getBaseSymbolDefinition,
  getCounterSymbol,
  getCounterSymbolDefinition,
  getCurrencies,
  getCurrenciesIndexed,
  getCurrenciesList,
  getCurrentInstrument,
  getCurrentLeverageRatio,
  getFavorites,
  getFiatCurrencies,
  getFiatCurrenciesList,
  getFiatCurrenciesListCompact,
  getInstrumentId,
  getInstruments,
  getInstrumentsIndexed,
  getPrecision,
  getPreviousInstrument,
  getRawCurrencies,
  getSymbol,
  getSymbolTyped,
  isSymbolBlocked
}
